// Generic "B-archetype" report PDF: gradient header + filter chips + KPI cards
// + a single themed table. Used by the reports under src/pages/reports/ that
// aren't day→application-block layouts. Reports map their existing
// kpis/columns/rows into this shape; PdfReportBuilderV2 renders it.

import type jsPDF from "jspdf";
import type { RowInput, UserOptions } from "jspdf-autotable";
import { PdfReportBuilderV2 } from "./PdfReportBuilderV2";
import { PDF_SECCION_NEUTRA, PDF_SECCION_ULTURE, PDF_V2 } from "./brand";
import type { FilterChipV2, KpiCard } from "./reportBlocks";
import type { MarcaInformes } from "@/lib/marca/tipos";

export interface ReportPdfColumn {
  header: string;
  /** width in mm; omit to let the column auto-size. */
  width?: number;
  align?: "left" | "right";
  /** lighter body text (secondary identifiers). */
  muted?: boolean;
  /** Drop this column entirely when every row is empty ("—"/""/null). */
  hideIfEmpty?: boolean;
}

export interface ReportPdfOptions {
  orientation?: "portrait" | "landscape";
  companyName: string;
  /** Header subtitle = the report name. */
  reportSubtitle: string;
  dateRange?: string;
  filters: FilterChipV2[];
  kpis: KpiCard[];
  columns: ReportPdfColumn[];
  rows: RowInput[];
  formatCurrency: (n: number) => string;
  formatNumber?: (n: number, d?: number) => string;
  footer: { generated: string; page: string; of: string; madeWith?: string };
  /** La marca de la empresa («Personalizar informes»), o null: el informe de siempre. */
  marca: MarcaInformes | null;
  /** Optional per-cell styler (status pills / risk colours), applied after defaults. */
  cellStyle?: UserOptions["didParseCell"];
  /**
   * Varias tablas en el mismo PDF, una debajo de otra, cada una con su título,
   * filtros y cifras (informes compuestos del asistente, 28-09-2026). Con
   * secciones, `filters`/`kpis`/`columns`/`rows` de arriba no se pintan.
   */
  sections?: ReportPdfSection[];
}

export interface ReportPdfSection {
  title: string;
  filters?: FilterChipV2[];
  kpis?: KpiCard[];
  columns: ReportPdfColumn[];
  rows: RowInput[];
}

export async function buildReportPdfDoc(opts: ReportPdfOptions): Promise<jsPDF> {
  const builder = new PdfReportBuilderV2({
    orientation: opts.orientation ?? "portrait",
    marca: opts.marca,
    brandSubtitle: opts.reportSubtitle,
    clientName: opts.companyName,
    dateRange: opts.dateRange,
    formatCurrency: opts.formatCurrency,
    formatNumber: opts.formatNumber,
    footer: opts.footer,
  });
  await builder.preload();
  builder.drawGradientHeader();
  if (opts.sections?.length) {
    for (const sec of opts.sections) {
      builder.drawDaySection(sec.title, String(sec.rows.length));
      if (sec.filters?.length) builder.drawFilterChips(sec.filters);
      if (sec.kpis?.length) builder.drawKpiCards(sec.kpis);
      drawOneTable(builder, sec.columns, sec.rows, opts.cellStyle, opts.marca != null);
    }
  } else {
    builder.drawFilterChips(opts.filters);
    builder.drawKpiCards(opts.kpis);
    drawOneTable(builder, opts.columns, opts.rows, opts.cellStyle, opts.marca != null);
  }

  builder.drawFootersOnAllPages();
  return builder.doc;
}

/** Una tabla del informe (columnas vacías fuera, cifras a la derecha). */
function drawOneTable(
  builder: PdfReportBuilderV2,
  columns: ReportPdfColumn[],
  bodyRows: RowInput[],
  cellStyle?: UserOptions["didParseCell"],
  conMarca = false,
): void {
  const opts = { columns, rows: bodyRows, cellStyle };
  // Drop hideIfEmpty columns that are blank across every row.
  const keep = opts.columns.map(
    (c, i) =>
      !c.hideIfEmpty ||
      opts.rows.some((r) => {
        if (!Array.isArray(r)) return true; // object rows count as content
        const v = r[i];
        return v != null && v !== "" && v !== "—";
      }),
  );
  const cols = opts.columns.filter((_, i) => keep[i]);
  const rows: RowInput[] = opts.rows.map((r) =>
    Array.isArray(r) ? (r.filter((_, i) => keep[i]) as RowInput) : r,
  );

  const numericIdx = new Set<number>();
  const mutedIdx = new Set<number>();
  const columnStyles: UserOptions["columnStyles"] = {};
  cols.forEach((c, i) => {
    const right = c.align === "right";
    if (right) numericIdx.add(i);
    if (c.muted) mutedIdx.add(i);
    columnStyles[i] = { ...(c.width ? { cellWidth: c.width } : {}), halign: right ? "right" : "left" };
  });

  builder.drawTable({
    head: [cols.map((c) => c.header.toUpperCase())],
    body: rows,
    columnStyles,
    didParseCell: (data) => {
      if (mutedIdx.has(data.column.index) && data.section === "body") {
        data.cell.styles.textColor = PDF_V2.color.materia;
      }
      // Numeric header AND cell right-aligned (autotable won't infer the head).
      if (numericIdx.has(data.column.index)) data.cell.styles.halign = "right";
      opts.cellStyle?.(data);
      // Con la marca de la empresa, las filas de sección sin el azul de Ulture.
      if (conMarca) neutralizarSeccion(data.cell.styles);
    },
  });
}

const mismo = (a: unknown, b: readonly number[]) => Array.isArray(a) && a.length === 3 && a.every((v, i) => v === b[i]);

/** La fila de sección en azul claro de Ulture → gris claro y tinta (informe con marca). */
function neutralizarSeccion(st: { fillColor?: unknown; textColor?: unknown }): void {
  if (!mismo(st.fillColor, PDF_SECCION_ULTURE.fill)) return;
  st.fillColor = [...PDF_SECCION_NEUTRA.fill];
  if (mismo(st.textColor, PDF_SECCION_ULTURE.text)) st.textColor = [...PDF_SECCION_NEUTRA.text];
}
